import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Radio, Youtube, Save, Eye, EyeOff, Clock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLivestream() {
  const [settings, setSettings] = useState({
    is_live: false,
    youtube_url: '',
    title: 'Live Worship Service',
    description: '',
    scheduled_time: '',
    reminder_message: 'Join us for our live service!'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/features/livestream`, { withCredentials: true });
      if (response.data) {
        setSettings({
          is_live: response.data.is_live || false,
          youtube_url: response.data.youtube_url || '',
          title: response.data.title || 'Live Worship Service',
          description: response.data.description || '',
          scheduled_time: response.data.scheduled_time || '',
          reminder_message: response.data.reminder_message || 'Join us for our live service!'
        });
      }
    } catch (error) {
      console.error('Failed to load livestream settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/features/livestream`, settings, { withCredentials: true });
      toast.success('Livestream settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleLive = async () => {
    const newIsLive = !settings.is_live;
    setSettings({ ...settings, is_live: newIsLive });
    try {
      await axios.put(`${API}/features/livestream`, { ...settings, is_live: newIsLive }, { withCredentials: true });
      toast.success(newIsLive ? 'Stream is now LIVE!' : 'Stream ended');
    } catch (error) {
      setSettings({ ...settings, is_live: !newIsLive });
      toast.error('Failed to update live status');
    }
  };

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return '';
    // Handle various YouTube URL formats
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    if (match && match[2].length === 11) {
      return `https://www.youtube.com/embed/${match[2]}`;
    }
    return url;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-livestream-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-serif text-charcoal">Livestream</h1>
          <p className="text-charcoal/60">Configure your live stream settings</p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-burgundy hover:bg-purple text-white"
        >
          <Save size={18} className="mr-2" /> {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>

      {/* Live Toggle Card */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className={`rounded-xl p-6 mb-6 ${settings.is_live ? 'bg-red-500' : 'bg-white border border-gold/10'}`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-16 h-16 rounded-xl flex items-center justify-center ${settings.is_live ? 'bg-white/20' : 'bg-red-100'}`}>
              <Radio size={32} className={settings.is_live ? 'text-white' : 'text-red-500'} />
            </div>
            <div>
              <h2 className={`text-xl font-serif ${settings.is_live ? 'text-white' : 'text-charcoal'}`}>
                {settings.is_live ? '🔴 LIVE NOW' : 'Currently Offline'}
              </h2>
              <p className={settings.is_live ? 'text-white/80' : 'text-charcoal/60'}>
                {settings.is_live ? 'Your stream is visible to visitors' : 'Click to go live'}
              </p>
            </div>
          </div>
          <Button
            onClick={toggleLive}
            className={settings.is_live ? 'bg-white text-red-500 hover:bg-white/90' : 'bg-red-500 hover:bg-red-600 text-white'}
            size="lg"
          >
            {settings.is_live ? <><EyeOff size={18} className="mr-2" /> End Stream</> : <><Eye size={18} className="mr-2" /> Go Live</>}
          </Button>
        </div>
      </motion.div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Settings Form */}
        <div className="bg-white rounded-xl p-6 border border-gold/10">
          <h3 className="font-serif text-xl text-charcoal mb-6">Stream Settings</h3>
          
          <div className="space-y-4">
            <div>
              <Label>YouTube URL *</Label>
              <div className="flex items-center gap-2">
                <Youtube size={20} className="text-red-500" />
                <Input
                  value={settings.youtube_url}
                  onChange={(e) => setSettings({...settings, youtube_url: e.target.value})}
                  placeholder="https://youtube.com/watch?v=..."
                  className="flex-1"
                />
              </div>
              <p className="text-xs text-charcoal/50 mt-1">
                Paste your YouTube video or live stream URL
              </p>
            </div>

            <div>
              <Label>Stream Title</Label>
              <Input
                value={settings.title}
                onChange={(e) => setSettings({...settings, title: e.target.value})}
                placeholder="Live Worship Service"
              />
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={settings.description}
                onChange={(e) => setSettings({...settings, description: e.target.value})}
                placeholder="Join us for worship..."
                rows={3}
              />
            </div>

            <div>
              <Label>Scheduled Time</Label>
              <div className="flex items-center gap-2">
                <Clock size={20} className="text-charcoal/50" />
                <Input
                  value={settings.scheduled_time}
                  onChange={(e) => setSettings({...settings, scheduled_time: e.target.value})}
                  placeholder="Sundays at 10:00 AM EST"
                />
              </div>
              <p className="text-xs text-charcoal/50 mt-1">
                Displayed when stream is offline
              </p>
            </div>

            <div>
              <Label>Reminder Message</Label>
              <Input
                value={settings.reminder_message}
                onChange={(e) => setSettings({...settings, reminder_message: e.target.value})}
                placeholder="Join us for our live service!"
              />
            </div>
          </div>
        </div>

        {/* Preview */}
        <div className="bg-white rounded-xl p-6 border border-gold/10">
          <h3 className="font-serif text-xl text-charcoal mb-6">Preview</h3>
          
          {settings.youtube_url ? (
            <div className="aspect-video rounded-xl overflow-hidden bg-charcoal">
              <iframe
                src={getYouTubeEmbedUrl(settings.youtube_url)}
                title={settings.title}
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : (
            <div className="aspect-video rounded-xl bg-cream flex items-center justify-center">
              <div className="text-center text-charcoal/50">
                <Youtube size={48} className="mx-auto mb-2 opacity-30" />
                <p>Enter a YouTube URL to see preview</p>
              </div>
            </div>
          )}
          
          <div className="mt-4">
            <h4 className="font-serif text-lg text-charcoal">{settings.title || 'Stream Title'}</h4>
            {settings.description && (
              <p className="text-charcoal/60 text-sm mt-1">{settings.description}</p>
            )}
            {!settings.is_live && settings.scheduled_time && (
              <p className="text-gold text-sm mt-2">
                <Clock size={14} className="inline mr-1" /> {settings.scheduled_time}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
